import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReactComponent as MailIcon } from '../../assets/mail.svg';
import Alert from '../Alert';
import BreadCrumbs from '../BreadCrumbs';
import StatusHandler from '../StatusHandler';
import Button from '../ui/Button';
import useApi from '../../hooks/useApi';
import {
  ClaimProvider,
  ClaimProviderDetailResponse,
  ClaimProviderInvite,
  ClaimProviderInvitesResponse,
} from '../../models/claimProvider';

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '-';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const SOCIAL_LINK_LABELS: Array<{ key: string; label: string }> = [
  { key: 'facebooks', label: 'Facebook' },
  { key: 'instagrams', label: 'Instagram' },
  { key: 'linkedIns', label: 'LinkedIn' },
  { key: 'twitters', label: 'Twitter / X' },
  { key: 'youtubes', label: 'YouTube' },
  { key: 'tiktoks', label: 'TikTok' },
  { key: 'pinterests', label: 'Pinterest' },
  { key: 'discords', label: 'Discord' },
];

const getSocialLinkUrls = (socialLinks: unknown, key: string): string[] => {
  if (!socialLinks || typeof socialLinks !== 'object') {
    return [];
  }
  const value = (socialLinks as Record<string, unknown>)[key];
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(url => (typeof url === 'string' ? url.trim() : ''))
    .filter(Boolean);
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'MM-dd-yyyy hh:mm a');
  } catch {
    return value;
  }
};

const DetailRow = ({
  label,
  value,
  pre = false,
}: {
  label: string;
  value: unknown;
  pre?: boolean;
}) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 border-b border-gray-100 py-3">
    <p className="text-sm font-semibold text-secondary-main">{label}</p>
    {pre ? (
      <pre className="md:col-span-2 text-sm text-gray-700 whitespace-pre-wrap break-words">
        {formatValue(value)}
      </pre>
    ) : (
      <p className="md:col-span-2 text-sm text-gray-700 break-words">
        {formatValue(value)}
      </p>
    )}
  </div>
);

const SocialLinksSection = ({ socialLinks }: { socialLinks: unknown }) => {
  const hasAnyLink = SOCIAL_LINK_LABELS.some(
    ({ key }) => getSocialLinkUrls(socialLinks, key).length > 0,
  );

  return (
    <section>
      <h2 className="text-xl font-semibold text-secondary-main mb-2">
        Social links
      </h2>
      {!hasAnyLink ? (
        <p className="text-sm text-gray-500 py-3">No social links available.</p>
      ) : (
        SOCIAL_LINK_LABELS.map(({ key, label }) => {
          const urls = getSocialLinkUrls(socialLinks, key);
          return (
            <div
              key={key}
              className="grid grid-cols-1 md:grid-cols-3 gap-2 border-b border-gray-100 py-3">
              <p className="text-sm font-semibold text-secondary-main">
                {label}
              </p>
              <div className="md:col-span-2 text-sm text-gray-700 break-words space-y-1">
                {urls.length ? (
                  urls.map(url => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-primary-main underline hover:opacity-80">
                      {url}
                    </a>
                  ))
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
          );
        })
      )}
    </section>
  );
};

const ClaimProviderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<ClaimProvider>();
  const [invites, setInvites] = useState<ClaimProviderInvite[]>([]);
  const [displayAlert, setDisplayAlert] = useState(false);

  const {
    response,
    isLoading,
    error,
    launchCall,
  } = useApi<ClaimProviderDetailResponse>(
    `admin/provider-discovery/providers/${id}`,
  );
  const {
    response: invitesResponse,
    launchCall: launchInvites,
  } = useApi<ClaimProviderInvitesResponse>(
    `admin/provider-discovery/providers/${id}/invites`,
  );
  const { launchCall: launchInvite, isLoading: isSending } = useApi(
    `admin/provider-discovery/providers/${id}/send-invite`,
  );

  useEffect(() => {
    if (!id) return;
    launchCall({ method: 'GET' });
    launchInvites({ method: 'GET' }, { page: 1, limit: 50 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (response?.data) {
      setProvider(response.data);
    }
  }, [response]);

  useEffect(() => {
    if (invitesResponse?.data) {
      setInvites(invitesResponse.data);
    }
  }, [invitesResponse]);

  const handleSendInvite = async () => {
    const result = await launchInvite({ method: 'POST' });
    setDisplayAlert(false);
    if (result) {
      toast.success('Invite email sent successfully.');
      launchInvites({ method: 'GET' }, { page: 1, limit: 50 });
    } else {
      toast.error('Failed to send invite email.');
    }
  };

  const breadCrumbs = [
    { link: '/claim-providers', displayName: 'Claim Provider' },
    { displayName: provider?.name || 'Details' },
  ];

  return (
    <>
      {displayAlert && (
        <Alert
          title={`Send claim invite email to "${provider?.name}"?`}
          onClose={() => setDisplayAlert(false)}
          onConfirm={handleSendInvite}
        />
      )}
      <div className="bg-[#f7faff] py-6 px-4 min-h-screen space-y-4">
        <BreadCrumbs breadcrumbs={breadCrumbs} />
        <StatusHandler
          isLoading={isLoading}
          error={error ? 'Failed to load provider details.' : undefined}>
          {provider ? (
            <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-semibold text-secondary-main">
                    {provider.name}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    ID #{provider.id} · Review: {provider.reviewStatus} ·
                    Claimed: {provider.claimed ? 'Yes' : 'No'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    text="Edit"
                    className="w-auto"
                    onClick={() =>
                      navigate(`/claim-providers/${provider.id}/edit`)
                    }
                  />
                  <button
                    className="flex items-center px-3 py-2 border border-primary-main rounded-md disabled:opacity-50"
                    disabled={isSending || !provider.emails?.length}
                    onClick={() => setDisplayAlert(true)}>
                    <MailIcon className="fill-primary-main mr-2" />
                    <span className="text-primary-main font-semibold">
                      Send invite
                    </span>
                  </button>
                </div>
              </div>

              {provider.imageUrl ? (
                <img
                  src={provider.imageUrl}
                  alt={provider.name}
                  className="w-40 h-40 object-cover rounded-lg"
                />
              ) : null}

              <section>
                <h2 className="text-xl font-semibold text-secondary-main mb-2">
                  Contact & location
                </h2>
                <DetailRow label="Emails" value={provider.emails} />
                <DetailRow label="Phone" value={provider.phone} />
                <DetailRow label="Website" value={provider.website} />
                <DetailRow label="Address" value={provider.address} />
                <DetailRow label="City" value={provider.city} />
                <DetailRow label="City key" value={provider.cityKey} />
                <DetailRow label="State" value={provider.state} />
                <DetailRow label="Country" value={provider.country} />
                <DetailRow label="Postal code" value={provider.postalCode} />
                <DetailRow label="Latitude" value={provider.lat} />
                <DetailRow label="Longitude" value={provider.lng} />
                <DetailRow
                  label="Google Maps URL"
                  value={provider.googleMapsUrl}
                />
              </section>

              <section>
                <h2 className="text-xl font-semibold text-secondary-main mb-2">
                  Business details
                </h2>
                <DetailRow label="Sport" value={provider.sportId} />
                <DetailRow label="Rating" value={provider.rating} />
                <DetailRow label="Reviews count" value={provider.reviewsCount} />
                <DetailRow label="Categories" value={provider.categories} pre />
                <DetailRow label="Amenities" value={provider.amenities} pre />
                <DetailRow
                  label="Opening hours"
                  value={provider.openingHours}
                  pre
                />
              </section>

              <SocialLinksSection socialLinks={provider.socialLinks} />

              <section>
                <h2 className="text-xl font-semibold text-secondary-main mb-2">
                  Discovery metadata
                </h2>
                <DetailRow label="Source" value={provider.source} />
                <DetailRow
                  label="Source place ID"
                  value={provider.sourcePlaceId}
                />
                <DetailRow label="Source CID" value={provider.sourceCid} />
                <DetailRow label="Source FID" value={provider.sourceFid} />
                <DetailRow
                  label="Normalized name"
                  value={provider.normalizedName}
                />
                <DetailRow
                  label="Normalized website"
                  value={provider.normalizedWebsite}
                />
                <DetailRow
                  label="Normalized emails"
                  value={provider.normalizedEmails}
                />
                <DetailRow
                  label="Normalized phone"
                  value={provider.normalizedPhone}
                />
                <DetailRow
                  label="Discovery count"
                  value={provider.discoveryCount}
                />
                <DetailRow
                  label="First discovered"
                  value={formatDate(provider.firstDiscoveredAt)}
                />
                <DetailRow
                  label="Last seen"
                  value={formatDate(provider.lastSeenAt)}
                />
                <DetailRow
                  label="Created"
                  value={formatDate(provider.createdAt)}
                />
                <DetailRow
                  label="Updated"
                  value={formatDate(provider.updatedAt)}
                />
              </section>

              <section>
                <h2 className="text-xl font-semibold text-secondary-main mb-2">
                  Invite history
                </h2>
                {!invites.length ? (
                  <p className="text-sm text-gray-500">No invites sent yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-600">
                      <thead className="text-xs uppercase bg-gray-50 text-secondary-main">
                        <tr>
                          <th className="p-2">Email</th>
                          <th className="p-2">Status</th>
                          <th className="p-2">Sent at</th>
                          <th className="p-2">Expires at</th>
                          <th className="p-2">Used at</th>
                          <th className="p-2">Sent by admin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invites.map(invite => (
                          <tr
                            key={invite.id}
                            className="border-b border-gray-100">
                            <td className="p-2">{invite.sentToEmail}</td>
                            <td className="p-2">{invite.status}</td>
                            <td className="p-2">
                              {formatDate(invite.sentAt)}
                            </td>
                            <td className="p-2">
                              {formatDate(invite.expiresAt)}
                            </td>
                            <td className="p-2">
                              {formatDate(invite.usedAt)}
                            </td>
                            <td className="p-2">
                              {invite.sentByAdminId ?? '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <p className="text-gray-500">Provider not found.</p>
          )}
        </StatusHandler>
      </div>
    </>
  );
};

export default ClaimProviderDetail;
